import styled from "styled-components";
import { useAllUploadUnits } from "../../../graphql/hooks/documentHandler/useAllUploadUnits";
import type { InvoiceUploadUnitStatus } from "../../../models/InvoiceUploadUnitStatus";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import { theme } from "../../../theme";
import { Col, Row } from "react-grid-system";
import Checkbox from "../../atoms/inputs/Checkbox";
import type { InvoiceUploadUnitModel } from "../../../models/InvoiceUploadUnitModel";
import BodySmall from "../../atoms/typography/BodySmall";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import TextLinkButton from "../../atoms/buttons/TextLinkButton";
import { useState } from "react";
import type { DocumentViewerFiles } from "../../molecules/modals/PreviewDocumentModalContent";
import UploadUnitNotificationsComponents from "../../molecules/documentHandler/UploadUnitNotificationsComponents";
import {
  getDocumentUploadSource,
  UploadUnitStatusBadge,
} from "../../../utils/documentHandlerHelpers";
import ConnectUploadUnitToClientViaLookup from "../../molecules/documentHandler/ConnectUploadUnitToClientViaLookup";
import { DONE_STATUSES } from "../../templates/DocumentHandler";
import RejectDocumentButton from "../../molecules/documentHandler/RejectDocumentButton";
import DeleteDocumentButton from "../../molecules/documentHandler/DeleteDocumentButton";
import ConnectToClientButton from "../../molecules/documentHandler/ConnectToClientButton";
import { HorizontalSpacer } from "../../atoms/Spacers";
import TableHeaderText from "../../atoms/typography/TableHeaderText";
import Divider from "../../atoms/Divider";
import { MergeIcon } from "../../atoms/icons/MergeIcon";
import UploadUnitDocumentNotificationsComponents from "../../molecules/documentHandler/UploadUnitDocumentNotificationComponents";
import type { InvoiceServiceType } from "../../../models/InvoiceServiceType";
import InterpretWithoutTemplateButton from "../../molecules/documentHandler/InterpretWithoutTemplateButton";
import CreateNewTemplateButton from "../../molecules/documentHandler/CreateNewTemplateButton";
import UpdateDocumentSettingsButton from "../../molecules/documentHandler/UpdateDocumentSettingsButton";
import MarkAsHandledButton from "../../molecules/documentHandler/MarkAsHandledButton";
import DisplayUploadUnitEmailDetailsButton from "../../molecules/documentHandler/DisplayUploadUnitEmailButton";
import DisplayUploadUnitNotesButton, {
  type NotesDisplayModel,
} from "../../molecules/documentHandler/DisplayUploadUnitNotesButton";
import MergeFilesButton from "../../molecules/documentHandler/MergeFilesButton";
import LeaveInfoButton from "../../molecules/documentHandler/LeaveInfoButton";
import InterpretWithTemplateButton from "../../molecules/documentHandler/InterpretWithTemplateButton";
import type { InvoiceUploadDocumentStatus } from "../../../models/InvoiceUploadDocumentStatus";
import Modal from "../../molecules/Modal";
import PreviewDocumentsModalContent from "../../molecules/modals/PreviewDocumentModalContent";
import Alert from "../../molecules/Alert";
import CopyForDebuggingButton from "../../molecules/CopyForDebuggingButton";
import { ChevronSmallRightIcon } from "../../atoms/icons/ChevronIcons";
import { useNavigate } from "react-router-dom";
import Small from "../../atoms/typography/Small";

interface Props {
  clientIdentifier?: string;
  missingClientIdentifier?: boolean;
  sortDirection: "ASC" | "DESC";
  statuses: Array<InvoiceUploadUnitStatus>;
  onToggleDocument: (documentId: string, clientIdentifier?: string) => void;
  checkIfDocumentIsSelected: (documentId: string) => boolean;
  checkIfAllDocumentsInUploadUnitIsSelected: (
    uploadUnit: InvoiceUploadUnitModel,
  ) => boolean;
  onToggleUploadUnit: (
    uploadUnit: InvoiceUploadUnitModel,
    clientIdentifier?: string,
  ) => void;
  selectedDocuments: Array<string>;
  selectedClientId?: string;
  onClearSelection: () => void;
  maxUnitsPerClient?: number;
  totalCount?: number;
}

const UploadUnitSection = styled.div`
  padding: 0px 8px;
  border-bottom: 3px solid ${theme.colors.grey05};
`;

const GeneralInfoContainer = styled.div`
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const FlexedDiv = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const StyledDocumentRow = styled(Row)`
  padding: 8px 0px;
  margin: 8px 0px;
  &:hover {
    background-color: ${theme.colors.hover};
    cursor: pointer;
  }
`;

const getDocumentFinanceServiceType = (
  serviceType?: InvoiceServiceType,
  clientPreferredServiceType?: InvoiceServiceType,
) => {
  if (serviceType === "ADMINISTER_INVOICE") {
    return "serviceAdmin";
  } else if (serviceType === "SELL_INVOICE") {
    return "serviceFinance";
  } else if (clientPreferredServiceType === "ADMINISTER_INVOICE") {
    return "serviceAdmin";
  } else {
    return "serviceFinance";
  }
};

const DOCUMENT_SHOULD_BE_LOCKED_STATUSES: Array<InvoiceUploadDocumentStatus> = [
  "MANUALLY_MARKED_AS_HANDLED",
  "REGISTERED",
  "REJECTED",
  "SENT_FOR_INTERPRETATION",
];

const ClientUploadUnitExpanded: React.FC<Props> = ({
  clientIdentifier,
  missingClientIdentifier,
  sortDirection,
  statuses,
  onToggleDocument,
  checkIfDocumentIsSelected,
  checkIfAllDocumentsInUploadUnitIsSelected,
  onToggleUploadUnit,
  selectedDocuments,
  selectedClientId,
  onClearSelection,
  maxUnitsPerClient,
  totalCount,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clientUploadUnits, loading, error } = useAllUploadUnits(
    missingClientIdentifier ? true : undefined,
    statuses,
    maxUnitsPerClient,
    clientIdentifier,
    sortDirection,
    0,
    1,
  );

  const [selectedFiles, setSelectedFiles] = useState<
    Array<DocumentViewerFiles>
  >([]);
  const [documentPreviewOpen, setDocumentPreviewOpen] = useState(false);

  const invoiceUploadUnits =
    clientUploadUnits?.items[0]?.invoiceUploadUnits || [];
  const client = clientUploadUnits?.items[0]?.client;

  const onClickOpenDocuments = (
    event: React.MouseEvent,
    files: Array<DocumentViewerFiles>,
  ) => {
    event.stopPropagation();
    setSelectedFiles(files);
    setTimeout(() => {
      setDocumentPreviewOpen(true);
    }, 100);
  };

  //Helper functions
  const getFilesForDocumentPreview = (
    uploadUnitId: string,
  ): Array<DocumentViewerFiles> => {
    const uploadUnit = invoiceUploadUnits.find((x) => x.id === uploadUnitId);
    if (!uploadUnit) {
      alert(t("errors.missingUploadUnit"));
      return [];
    } else {
      const files =
        uploadUnit.invoiceDocuments?.map((doc) => ({
          fileName: doc.file?.fileName ?? t("documentHandler.fileIsMissing"),
          fileId: doc.file?.id ?? "Missing file ID",
          documentId: doc.id,
        })) || [];
      return files;
    }
  };

  const getCommentsForNotesPreview = (
    uploadUnitId: string,
  ): Array<NotesDisplayModel> => {
    const uploadUnit = invoiceUploadUnits.find((x) => x.id === uploadUnitId);
    if (!uploadUnit) {
      alert(t("errors.missingUploadUnit"));
      return [];
    } else {
      const notes =
        uploadUnit.invoiceDocuments?.map((doc) => ({
          comments: doc.comments?.items ?? [],
          totalCount: doc.comments?.totalCount || 0,
          fileName: doc.file?.fileName ?? t("documentHandler.fileIsMissing"),
        })) || [];
      return notes;
    }
  };

  const getDocumentsForInterpretation = (): Array<{
    uploadUnitId: string;
    uploadDocumentIds: Array<string>;
  }> => {
    const documentsForInterpretation: Array<{
      uploadUnitId: string;
      uploadDocumentIds: Array<string>;
    }> = [];
    if (selectedDocuments.length === 0) return documentsForInterpretation;

    const uploadUnitMap: { [key: string]: Array<string> } = {};

    invoiceUploadUnits.forEach((i) =>
      i.invoiceDocuments?.forEach((d) => {
        if (selectedDocuments.includes(d.id)) {
          if (!uploadUnitMap[i.id]) {
            uploadUnitMap[i.id] = [];
          }
          uploadUnitMap[i.id].push(d.id);
        }
      }),
    );

    for (const uploadUnitId in uploadUnitMap) {
      documentsForInterpretation.push({
        uploadUnitId: uploadUnitId,
        uploadDocumentIds: uploadUnitMap[uploadUnitId],
      });
    }

    return documentsForInterpretation;
  };

  return (
    <div>
      {loading && <LoadingSpinner />}

      {error && (
        <Alert
          type="error"
          title={t("errors.couldNotFetchUploadDocuments")}
          message={error?.toString()}
        />
      )}

      <SlideOutHeight isVisible={clientUploadUnits !== undefined}>
        <div>
          {invoiceUploadUnits.map((uploadUnit) => (
            <UploadUnitSection key={uploadUnit.id}>
              <GeneralInfoContainer>
                <div>
                  <div
                    style={{ display: "flex", gap: 24, alignItems: "flex-end" }}
                  >
                    <div style={{ minWidth: 250 }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <BodySmall fontweight={500}>
                          {t(getDocumentUploadSource(uploadUnit.uploadType))}
                        </BodySmall>
                        <BodySmall fontweight={500}>
                          {uploadUnit.emailDetails?.sentFromEmail}
                        </BodySmall>
                      </div>
                      <Small fontweight={400}>
                        {dayjs(uploadUnit.createdAt).format("YYYY-MM-DD")}{" "}
                        {dayjs(uploadUnit.createdAt).format("HH:mm")}
                      </Small>
                      <Small fontweight={400}>
                        {client?.name} {client?.id}
                      </Small>
                    </div>

                    <div
                      style={{
                        minWidth: 250,
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <div>
                        <UploadUnitStatusBadge
                          status={uploadUnit.status}
                          statusHistory={uploadUnit.statusHistory}
                        />
                      </div>
                      <FlexedDiv>
                        {uploadUnit.emailDetails && (
                          <UploadUnitNotificationsComponents
                            type="mail"
                            emailContent={uploadUnit.emailDetails}
                          />
                        )}
                        {uploadUnit?.isBlacklisted && (
                          <UploadUnitNotificationsComponents type="blacklist" />
                        )}
                        {uploadUnit?.invalidAttachments &&
                          uploadUnit?.invalidAttachments?.length > 0 && (
                            <UploadUnitNotificationsComponents type="invalidFiles" />
                          )}
                      </FlexedDiv>
                    </div>

                    <div style={{ minWidth: 100 }}>
                      <TextLinkButton
                        title={`${uploadUnit?.invoiceDocuments?.length} ${t(
                          "documentHandler.document",
                        ).toLowerCase()}`}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                          onClickOpenDocuments(
                            e,
                            getFilesForDocumentPreview(uploadUnit.id),
                          )
                        }
                        color={theme.colors.primary}
                      />
                    </div>
                    {uploadUnit.rejectionComment && (
                      <UploadUnitNotificationsComponents
                        type="rejectionComment"
                        rejection={uploadUnit.rejectionComment}
                      />
                    )}
                  </div>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 16,
                    alignItems: "flex-end",
                  }}
                >
                  {!client && (
                    <ConnectUploadUnitToClientViaLookup
                      uploadUnitId={uploadUnit.id}
                    />
                  )}
                  <CopyForDebuggingButton copyValue={uploadUnit.id} />

                  {!DONE_STATUSES.includes(uploadUnit.status) &&
                    uploadUnit.status !== "PREPROCESSED" && (
                      <>
                        <RejectDocumentButton
                          isDocument={false}
                          uploadUnitId={uploadUnit.id}
                          onClearSelection={onClearSelection}
                          emailSuggestion={
                            uploadUnit.emailDetails?.sentFromEmail
                          }
                        />
                        <DeleteDocumentButton
                          isDocument={false}
                          uploadUnitId={uploadUnit.id}
                        />
                      </>
                    )}
                  <ConnectToClientButton
                    email={uploadUnit.emailDetails?.sentFromEmail}
                    uploadUnitId={uploadUnit.id}
                    clientIdentifier={client?.id}
                  />
                </div>
              </GeneralInfoContainer>

              <HorizontalSpacer spacing={1} />
              <div style={{ padding: "0 8px" }}>
                <div>
                  <Row
                    align="center"
                    style={{
                      backgroundColor: theme.colors.grey02,
                      padding: "4px 0",
                    }}
                  >
                    <Col
                      lg={2}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <Checkbox
                        checked={checkIfAllDocumentsInUploadUnitIsSelected(
                          uploadUnit,
                        )}
                        onChange={() =>
                          onToggleUploadUnit(uploadUnit, clientIdentifier)
                        }
                      />
                      <TableHeaderText>{t("common.document")}</TableHeaderText>
                    </Col>
                    <Col lg={1.5}>
                      <TableHeaderText> {t("common.status")}</TableHeaderText>
                    </Col>
                    <Col lg={3.5}>
                      <TableHeaderText>
                        {t("documentHandler.notifications")}
                      </TableHeaderText>
                    </Col>
                    <Col lg={2}>
                      <TableHeaderText>
                        {t("documentHandler.info")}
                      </TableHeaderText>
                    </Col>
                    <Col lg={3}>
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <TableHeaderText>
                          {" "}
                          {t("documentHandler.quickActions")}
                        </TableHeaderText>
                      </div>
                    </Col>
                  </Row>
                </div>

                {uploadUnit.invoiceDocuments?.map((document) => (
                  <StyledDocumentRow
                    align="center"
                    key={document.id}
                    onClick={() => onToggleDocument(document.id, client?.id)}
                  >
                    <Col
                      lg={2}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <Checkbox
                        checked={checkIfDocumentIsSelected(document.id)}
                        onChange={() =>
                          onToggleDocument(document.id, client?.id)
                        }
                      />
                      <TextLinkButton
                        title={
                          document.file?.fileName ??
                          t("documentHandler.fileIsMissing")
                        }
                        color={theme.colors.primary}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                          onClickOpenDocuments(e, [
                            {
                              fileName:
                                document.file?.fileName ??
                                t("documentHandler.fileIsMissing"),
                              fileId:
                                document.file?.id ??
                                t("documentHandler.fileIsMissing"),
                              documentId: document.id,
                            },
                          ])
                        }
                        icon={
                          document?.relatedDocuments &&
                          document.relatedDocuments.length > 0 && (
                            <MergeIcon color={theme.colors.primary} />
                          )
                        }
                        disabled={!document.file}
                      />
                    </Col>
                    <Col lg={1.5}>
                      <UploadUnitStatusBadge status={document.status} />
                    </Col>
                    <Col lg={3.5}>
                      <FlexedDiv>
                        {document.userMessage && (
                          <UploadUnitDocumentNotificationsComponents
                            type="customerComment"
                            comment={document.userMessage}
                          />
                        )}
                        {document?.invoiceRecipientEmails &&
                          document.invoiceRecipientEmails.length > 0 && (
                            <UploadUnitDocumentNotificationsComponents
                              type="recipientAddress"
                              recipientEmails={document.invoiceRecipientEmails}
                            />
                          )}
                        {document.invoiceRecipientGln && (
                          <UploadUnitDocumentNotificationsComponents
                            type="gln"
                            gln={document.invoiceRecipientGln}
                          />
                        )}
                        {document?.comments &&
                          document.comments?.totalCount > 0 && (
                            <UploadUnitDocumentNotificationsComponents
                              type="comments"
                              comments={document.comments?.items}
                            />
                          )}
                        <UploadUnitDocumentNotificationsComponents
                          type={getDocumentFinanceServiceType(
                            document.serviceType,
                            client?.settings?.product
                              ?.defaultInvoiceServiceType,
                          )}
                        />
                      </FlexedDiv>
                    </Col>
                    <Col lg={2}>
                      {document?.relatedDocuments &&
                        document.relatedDocuments.length > 0 && (
                          <Small>
                            {t("documentHandler.theDocumentIsMerged")}
                          </Small>
                        )}
                    </Col>
                    <Col lg={3}>
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 16,
                          alignItems: "center",
                        }}
                      >
                        <CopyForDebuggingButton copyValue={document.id} />
                        {client &&
                          !DOCUMENT_SHOULD_BE_LOCKED_STATUSES.includes(
                            document.status,
                          ) && (
                            <InterpretWithoutTemplateButton
                              documentId={document.id}
                              uploadUnitId={uploadUnit.id}
                            />
                          )}
                        {client && (
                          <CreateNewTemplateButton
                            clientIdentifier={client.id}
                            fileId={document.file?.id ?? "Missing file ID"}
                            disabled={!document.file}
                          />
                        )}
                        {!DOCUMENT_SHOULD_BE_LOCKED_STATUSES.includes(
                          document.status,
                        ) && (
                          <>
                            <UpdateDocumentSettingsButton
                              documentId={document.id}
                              serviceTypeInitial={document.serviceType}
                              recipientGlnInitial={document.invoiceRecipientGln}
                              recipientEmailsInitial={
                                document.invoiceRecipientEmails
                              }
                            />
                            <DeleteDocumentButton
                              isDocument={true}
                              documentId={document.id}
                            />
                            <RejectDocumentButton
                              isDocument={true}
                              documentId={document.id}
                              onClearSelection={onClearSelection}
                              fileId={document.file?.id}
                              emailSuggestion={
                                uploadUnit.emailDetails?.sentFromEmail
                              }
                            />
                            <MarkAsHandledButton
                              documentId={document.id}
                              clientIdentifier={client?.id ?? ""}
                            />
                          </>
                        )}
                      </div>
                    </Col>
                  </StyledDocumentRow>
                ))}
              </div>

              <Divider />
              <div
                style={{
                  display: "flex",
                  gap: 24,
                  height: "fit-content",
                  alignItems: "baseline",
                  justifyContent: "flex-end",
                  padding: 8,
                }}
              >
                {uploadUnit?.emailDetails && (
                  <DisplayUploadUnitEmailDetailsButton
                    emailDetails={uploadUnit.emailDetails}
                  />
                )}
                <DisplayUploadUnitNotesButton
                  notes={getCommentsForNotesPreview(uploadUnit.id)}
                />
                <MergeFilesButton
                  selectedFiles={getFilesForDocumentPreview(uploadUnit.id)}
                  uploadUnitId={uploadUnit.id}
                />

                <LeaveInfoButton
                  clientIdentifier={client?.id}
                  documents={uploadUnit.invoiceDocuments}
                />
                {client && !DONE_STATUSES.includes(uploadUnit.status) && (
                  <InterpretWithTemplateButton
                    documentsForInterpretation={getDocumentsForInterpretation()}
                    variant="small"
                    clientTemplates={client.invoiceReaderTemplates}
                    clientIdentifier={client.id}
                    selectedClientId={selectedClientId}
                    onClearSelection={onClearSelection}
                    selectedDocuments={selectedDocuments}
                  />
                )}
              </div>
            </UploadUnitSection>
          ))}
        </div>
        {maxUnitsPerClient && totalCount && maxUnitsPerClient < totalCount && (
          <Row nogutter justify="end" style={{ margin: 8 }}>
            <TextLinkButton
              title={t("documentHandler.showMoreRegisteredDocuments")}
              icon={
                <ChevronSmallRightIcon color={theme.colors.textSecondary} />
              }
              onClick={() =>
                navigate(`/client/${clientIdentifier}/invoicesNew`)
              }
              color={theme.colors.textSecondary}
            />
          </Row>
        )}
      </SlideOutHeight>

      {documentPreviewOpen && (
        <Modal
          onClose={() => setDocumentPreviewOpen(false)}
          closeIcon
          width={1200}
        >
          <PreviewDocumentsModalContent files={selectedFiles} />
        </Modal>
      )}
    </div>
  );
};

export default ClientUploadUnitExpanded;
